/**
 * The shot store: everything the camera has captured, on the device.
 *
 * A shot keeps **two** images. `original` is the frame exactly as the camera
 * produced it, and `rendered` is that frame with a preset and a gain applied.
 * Keeping the original is what makes a shot re-editable: changing the preset
 * later re-runs the stretch from the untouched pixels instead of stretching an
 * already-stretched image, which would compound the colour shift.
 *
 * Storage is IndexedDB, not the Cache API and not localStorage. A 4K PNG is
 * megabytes; localStorage holds strings only and caps at about 5 MB, and the
 * Cache API is for requests and responses, not for application data. IndexedDB
 * stores `Blob`s directly, and unchanged blobs are not copied into memory.
 *
 * The `thumb` is a small encode of the rendered image, kept because the shots
 * panel shows a strip of them and decoding a full-resolution PNG per tile is
 * what makes such a panel janky.
 *
 * This module is the only place that names the database, so the schema lives in
 * one file.
 *
 * @module web/js/shot-store
 */

/** One captured frame and its current render. */
export interface Shot {
  /** Assigned by IndexedDB on first write. Absent until then. */
  id?: number;
  /** When the frame was captured, in milliseconds since the epoch. */
  createdAt: number;
  width: number;
  height: number;
  /** The preset id the render used. */
  preset: string;
  /** The band-gain multiplier the render used. `1` is the preset as written. */
  gain: number;
  /** PNG of the untouched capture. */
  original: Blob;
  /** PNG of the stretched capture. */
  rendered: Blob;
  /** Small preview of the rendered capture, for the strip. */
  thumb: Blob;
}

const DB_NAME = 'ochre';
const DB_VERSION = 1;
const STORE = 'shots';

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open the database, once per page.
 *
 * The promise is cached, and a failed open clears the cache so that a later
 * call can try again — a transient failure (a private-mode quota refusal) must
 * not poison the rest of the session.
 */
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      dbPromise = null;
      reject(new Error('IndexedDB is unavailable in this browser'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        // The panel is newest-first, and this is the only order it ever asks for.
        store.createIndex('createdAt', 'createdAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error('could not open the shot store'));
    };
    request.onblocked = () => {
      dbPromise = null;
      reject(new Error('the shot store is blocked by another tab'));
    };
  });
  return dbPromise;
}

/** Run `fn` in one read-write transaction, resolving with its request. */
function write<T>(
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const request = fn(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('shot store write failed'));
        tx.onabort = () => reject(tx.error ?? new Error('shot store transaction aborted'));
      }),
  );
}

/** Run `fn` in one read-only transaction, resolving with its request. */
function read<T>(fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const request = fn(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('shot store read failed'));
      }),
  );
}

/** Store a new shot and return it with the id IndexedDB assigned. */
export async function addShot(shot: Shot): Promise<Shot> {
  const id = await write((store) => store.add(shot) as IDBRequest<IDBValidKey>);
  return { ...shot, id: Number(id) };
}

/** Replace a shot's stored copy, matched by id. */
export async function putShot(shot: Shot): Promise<void> {
  if (shot.id === undefined) throw new Error('putShot needs a shot with an id');
  await write((store) => store.put(shot) as IDBRequest<IDBValidKey>);
}

/** Delete one shot. */
export async function deleteShot(id: number): Promise<void> {
  await write((store) => store.delete(id) as IDBRequest<undefined>);
}

/** Every shot, newest first. */
export async function listShots(): Promise<Shot[]> {
  const all = await read((store) => store.getAll() as IDBRequest<Shot[]>);
  return all.sort((a, b) => b.createdAt - a.createdAt);
}

/** One shot, or `undefined`. */
export async function getShot(id: number): Promise<Shot | undefined> {
  return read((store) => store.get(id) as IDBRequest<Shot | undefined>);
}

/** How many shots are stored. */
export async function countShots(): Promise<number> {
  return read((store) => store.count() as IDBRequest<number>);
}
